import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNewTraderGuide } from "../../context/NewTraderGuideContext";

const GUIDE_ASSETS = [
  { symbol: "X:BTCUSD", name: "Bitcoin",  shortName: "Bitcoin",  bg: "#F7931A", icon: "/bitcoin.svg", iconStyle: { width: 50, height: 50 } },
  { symbol: "AAPL",     name: "Apple",    shortName: "Apple",    bg: "#555555", icon: "/apple.svg",   iconStyle: { width: 40, height: 50 } },
  { symbol: "SPY",      name: "S&P 500",  shortName: "S&P 500",  bg: "#C00000", label: "S&P\n500",    iconStyle: {} },
];

const PERSONA_CONTENT = {
  new: {
    totalDots: 5,
    step1: {
      heading: "Everyone starts somewhere",
      subtext: "Let's place your first practice trade",
      footer: "Pick one asset.\nWe'll use real market data and virtual money.",
    },
    step2: {
      heading: (asset) => `This is the live daily chart of ${asset.name}`,
      subtext: "Each candle shows one day of price movement.\nFor now, just ask: up, down, or sideways?",
      buttonText: "Place a practice trade",
    },
    step4: {
      heading: "You just placed your first trade ever",
      subtext: "Now let's show you where it lives.",
    },
    step5: {
      heading: "Your trade is live",
      subtext: "Every trade you make lives here. Check back later and see how the market treated you",
      infoLines: [
        { icon: "info", text: "Want a quick page tour? Close this, then tap the" },
      ],
      buttonText: "Got it, let's explore",
    },
    hasStep5: true,
  },
  basics: {
    totalDots: 4,
    step1: {
      heading: "You know the basics",
      subtext: "Let's build some reps",
      footer: "Pick an asset.\nReal market data. Virtual money.",
    },
    step4: {
      heading: "That's rep one",
      subtext: "You can track it, manage it,\nand learn from the results\nin your portfolio.",
    },
    step5: {
      heading: "Now it gets interesting",
      subtext: " See how price moves against your entry. This is how real learning happens.",
      infoLines: [
        { icon: "info", text: "Want a quick page tour?\nClose this, then tap the" },
      ],
      buttonText: "Got it",
    },
    hasStep5: true,
  },
  experienced: {
    totalDots: 5,
    step1: {
      heading: "You came to compete",
      subtext: "Let's show you how traders rank on QUSHi.\nTrack performance, compare results,\nand let your trades speak.",
      buttonText: "View leaderboard",
      nextRoute: "/leaderboard",
      nextStep: 2,
    },
    step2: {
      heading: "This is the scoreboard",
      subtext: "Compare traders across any timeframe. Tap any name to see their full record.",
      buttonText: "Explore League Play",
      nextRoute: "/contest",
      nextStep: 4,
      secondaryText: "Or tap a trader",
      // No secondaryRoute — just dismiss the popup so the user can tap any
      // trader card on the leaderboard themselves. Step 3 ("This is trader proof")
      // auto-renders on /profile/:userId when guideStep === 3.
      secondaryStep: 3,
    },
    step3: {
      heading: "This is trader proof",
      subtext: "Holdings, trade history, win rate, and more. Keep participating to unlock the full picture. This is how QUSHi separates results from talk.",
      buttonText: "Explore League Play",
      nextRoute: "/contest",
      nextStep: 4,
    },
    step4: {
      heading: "This is League Play",
      subtext: "Create or join public and private leagues with friends, clubs, or classmates. Your league results stay separate from your main public record.",
      buttonText: "Start competing",
      nextRoute: "/charts",
      nextStep: 5,
      setTicker: "X:BTCUSD",
    },
    step5: {
      heading: "Trade when ready",
      subtext: "Use the chart and timeframes to study\nyour setup and choose your entry.",
      infoLines: [
        { icon: "shield", text: "Your main public record starts with\nthe trades you decide to place." },
        { icon: "info", text: "Want a quick page tour? Close this, then\ntap the green tutorial icon on the chart page." },
      ],
      buttonText: "Got it",
    },
  },
  creator: {
    totalDots: 7,
    step1: {
      heading: "Your profile is your proof",
      subtext: "This is where your posts, followers, trades,\nand performance can build trust over time.\nMake it feel like you.",
      buttonText: "Edit profile",
      nextRoute: "/edit-profile",
      // No nextStep — guide stays at step 1 while user is on /edit-profile.
      // Step 2 ("Nice start") only advances after a successful profile save
      // (handled in EditProfile.jsx). This way the back button returns the
      // user to /profile with step 1 still visible.
      secondaryText: "Skip for now",
      secondaryRoute: "/leaderboard",
      secondaryStep: 3,
    },
    step2: {
      heading: "You're already ahead of most",
      subtext: "Your profile helps people understand\nwho you are and why they should follow you.",
      buttonText: "See how traders get discovered",
      nextRoute: "/leaderboard",
      nextStep: 3,
    },
    step3: {
      heading: "Get discovered through proof",
      subtext: "Performance and consistency speak louder than hype. \nYour track record is your fastest path to followers.",
      buttonText: "View a top trader",
      // Dynamic route — resolved at click time to the user at #1 on the leaderboard
      // (written to localStorage by LeaderboardSecton). Falls back to /profile/534
      // if no leaderboard data has been seen yet.
      useTopTrader: true,
      nextStep: 4,
    },
    step4: {
      heading: "This is trader proof",
      subtext: "See any trader's holdings, trade history, and performance. Keep showing up to unlock the full picture. \nThat is how trust gets built on QUSHi.",
      buttonText: "Go to feed",
      nextRoute: "/feed",
      nextStep: 5,
    },
    step5: {
      heading: "Show how you think",
      subtext: "Use the feed to share trade ideas, chart notes,\nlessons, or market thoughts. Good content\nhelps people understand your style.",
      buttonText: "See how easy to share a chart",
      nextRoute: "/charts",
      nextStep: 6,
      setTicker: "X:BTCUSD",
    },
    step6: {
      heading: "Turn charts into content",
      subtext: "Use the chart to explain what you see.\nShare your view to the feed so other traders\ncan follow your thinking.",
      buttonText: "Create chart post",
      secondaryText: "Skip for now",
      nextStep: 7,
      triggerShareModal: true,
    },
    step7: {
      heading: "This is how it starts",
      subtext: "Followers come for your ideas.\nThey stay when your record backs them up.\n\nKeep sharing what you see.",
      buttonText: "Post to feed",
      secondaryText: "Skip for now",
    },
  },
};

function StepDots({ active, total }) {
  return (
    <div className="flex justify-center gap-2 mb-6">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className="rounded-full transition-all"
          style={{
            width: i < active ? 30 : 30,
            height: 6,
            background: i < active ? "#35b23c" : "#D1D5DB",
            display: "inline-block",
          }}
        />
      ))}
    </div>
  );
}

function getPersona() {
  return localStorage.getItem("qushi_onboarding_persona");
}

function getPersonaContent() {
  return PERSONA_CONTENT[getPersona()] || PERSONA_CONTENT.new;
}

function getStageForStep(step, persona) {
  if (persona === "basics") {
    return { 1: 1, 3: 2, 4: 3, 5: 4 }[step] || 1;
  }
  return step;
}

function SheetWrapper({ children, dim = true }) {
  const { completeGuide } = useNewTraderGuide();
  const [dragY, setDragY] = useState(0);
  const [dragStart, setDragStart] = useState(null);
  const [isPeeked, setIsPeeked] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const PEEK_OFFSET = 260;
  const SNAP_THRESHOLD = 60;

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const baseOffset = isPeeked ? PEEK_OFFSET : 0;

  const handleDragStart = (clientY) => {
    setDragStart(clientY);
  };

  const handleDragMove = (clientY) => {
    if (dragStart === null) return;
    const delta = clientY - dragStart;
    // Allow dragging in both directions, clamped to non-negative offset
    const next = Math.max(0, baseOffset + delta);
    setDragY(next - baseOffset);
  };

  const handleDragEnd = () => {
    if (dragStart === null) return;
    const finalOffset = baseOffset + dragY;
    if (isPeeked) {
      // Coming from peek: snap to full if dragged up enough, else stay peeked
      if (finalOffset < PEEK_OFFSET - SNAP_THRESHOLD) {
        setIsPeeked(false);
      }
    } else {
      // Coming from full: snap to peek if dragged down enough, else stay full
      if (finalOffset > SNAP_THRESHOLD) {
        setIsPeeked(true);
      }
    }
    setDragY(0);
    setDragStart(null);
  };

  const sheetOffset = baseOffset + dragY;

  if (isDesktop) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: dim ? "rgba(0,0,0,0.4)" : "transparent",
          pointerEvents: dim ? "auto" : "none",
        }}
      >
        <div
          style={{
            position: "relative",
            background: "#fff",
            borderRadius: 20,
            padding: "40px 48px",
            maxWidth: 520,
            width: "90%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            pointerEvents: "auto",
          }}
        >
          <button
            onClick={completeGuide}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "none",
              background: "#F3F4F6",
              cursor: "pointer",
              fontSize: 18,
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
          >
            ✕
          </button>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        pointerEvents: dim && !isPeeked ? "auto" : "none",
      }}
    >
      <div
        style={{
          flex: 1,
          background: dim && !isPeeked ? "rgba(0,0,0,0.35)" : "transparent",
          transition: "background 0.25s ease-out",
          pointerEvents: dim && !isPeeked ? "auto" : "none",
        }}
      />
      <div
        className="steps-popup"
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: "20px 20px 0 0",
          padding: "28px 24px 40px",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.12)",
          transform: `translateY(${sheetOffset}px)`,
          transition: dragStart === null ? "transform 0.3s ease-out" : "none",
          pointerEvents: "auto",
        }}
      >
        <div
          onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientY)}
          onTouchEnd={handleDragEnd}
          onMouseDown={(e) => handleDragStart(e.clientY)}
          onMouseMove={(e) => {
            if (dragStart !== null) handleDragMove(e.clientY);
          }}
          onMouseUp={handleDragEnd}
          onMouseLeave={() => {
            if (dragStart !== null) handleDragEnd();
          }}
          style={{
            width: "100%",
            padding: "16px 0",
            display: "flex",
            justifyContent: "center",
            margin: "-22px 0 8px",
            cursor: "grab",
            touchAction: "none",
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              background: "#D1D5DB",
              borderRadius: 99,
              pointerEvents: "none",
            }}
          />
        </div>
        {children}
      </div>
    </div>
  );
}

function CtaSheet({ stage, totalDots, heading, subtext, buttonText, onButton, secondaryText, onSecondary, infoLines }) {
  return (
    <SheetWrapper>
      <StepDots active={stage} total={totalDots} />
      <h2
        className="font-bold text-center text-primaryColor"
        style={{ fontSize: 30, marginBottom: 12 }}
      >
        {heading}
      </h2>
      <p
        className="text-center whitespace-pre-line text-primaryColor"
        style={{ fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}
      >
        {subtext}
      </p>

      {infoLines && (
        <div style={{ marginBottom: 24 }}>
          {infoLines.map((line, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 10,
                padding: "20px 4px",
                borderTop: i > 0 ? "1px solid #E5E7EB" : "none",
              }}
            >
              {line.icon === "shield" && (
                <img
                  src="/shield.svg"
                  alt="shield"
                  style={{ width: 28, height: 28, flexShrink: 0, marginTop: 2 }}
                />
              )}
              <p
                className=" whitespace-pre-line"
                style={{ color: "#004c50", fontSize: 13, lineHeight: 1.5, margin: 0 }}
              >
                {line.text}
                {line.icon !== "shield" && (
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "2px solid #35b23c",
                      color: "#35b23c",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 16,
                      fontFamily: "Georgia, serif",
                      lineHeight: 1,
                      flexShrink: 0,
                      marginLeft: 8,
                      verticalAlign: "middle",
                    }}
                  >
                    i
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onButton}
        style={{
          width: "100%",
          background: "#004c50",
          color: "#fff",
          borderRadius: 100,
          padding: "16px 0",
          fontWeight: 600,
          fontSize: 17,
          border: "none",
          cursor: "pointer",
        }}
        className=""
      >
        {buttonText}
      </button>

      {secondaryText && (
        <button
          onClick={onSecondary}
          style={{
            width: "100%",
            background: "transparent",
            color: "#6B7280",
            border: "none",
            cursor: "pointer",
            fontSize: 14,
            padding: "12px 0 0",
          }}
          className=""
        >
          {secondaryText}
        </button>
      )}
    </SheetWrapper>
  );
}

export default function NewTraderGuide({ step }) {
  const navigate = useNavigate();
  const { selectedAsset, setSelectedAsset, advanceStep, completeGuide } =
    useNewTraderGuide();
  const persona = getPersona();
  const content = getPersonaContent();
  const stage = getStageForStep(step, persona);
  const totalDots = content.totalDots || 4;

  // ───────── linear CTA flow (experienced + creator) ─────────
  if (persona === "experienced" || persona === "creator") {
    const stepContent = content[`step${step}`];
    if (!stepContent) return null;

    const handleNext = () => {
      if (stepContent.triggerShareModal) {
        window.dispatchEvent(new CustomEvent("qushi:open-share-modal"));
      }
      if (stepContent.setTicker) {
        localStorage.setItem("ticker", stepContent.setTicker);
      }
      if (stepContent.nextStep) {
        advanceStep(stepContent.nextStep);
      }
      // Resolve dynamic top-trader route at click time
      let resolvedRoute = stepContent.nextRoute;
      if (stepContent.useTopTrader) {
        const topId = localStorage.getItem("qushi_top_trader_id");
        resolvedRoute = topId ? `/profile/${topId}` : "/profile/534";
      }
      if (resolvedRoute) {
        navigate(resolvedRoute);
      }
      if (!stepContent.nextStep && !resolvedRoute && !stepContent.triggerShareModal) {
        completeGuide();
      }
    };

    const handleSecondary = () => {
      if (stepContent.secondaryStep || stepContent.secondaryRoute) {
        if (stepContent.secondaryStep) advanceStep(stepContent.secondaryStep);
        if (stepContent.secondaryRoute) navigate(stepContent.secondaryRoute);
      } else {
        completeGuide();
      }
    };

    return (
      <CtaSheet
        stage={stage}
        totalDots={totalDots}
        heading={stepContent.heading}
        subtext={stepContent.subtext}
        buttonText={stepContent.buttonText}
        onButton={handleNext}
        secondaryText={stepContent.secondaryText}
        onSecondary={stepContent.secondaryText ? handleSecondary : undefined}
        infoLines={stepContent.infoLines}
      />
    );
  }

  // ───────── new / basics personas ─────────
  const handleAssetPick = (asset) => {
    setSelectedAsset(asset);
    localStorage.setItem("ticker", asset.symbol);
    if (persona === "basics") {
      advanceStep(3);
    } else {
      advanceStep(2);
    }
    navigate("/charts");
  };

  if (step === 1) {
    const c = content.step1;
    return (
      <SheetWrapper>
        <StepDots active={stage} total={totalDots} />
        <h2
          className="font-bold text-center  text-primaryColor"
          style={{ fontSize: 30, marginBottom: 6 }}
        >
          {c.heading}
        </h2>
        <p
          className="text-center  text-primaryColor "
          style={{fontSize: 15, marginBottom: 28 }}
        >
          {c.subtext}
        </p>

        <div className="flex gap-3 justify-center w-[92%] mx-auto sm:w-auto">
          {GUIDE_ASSETS.map((asset) => (
            <button
              key={asset.symbol}
              onClick={() => handleAssetPick(asset)}
              style={{
                flex: 1,
                border: "2px solid #e7e7e7",
                borderRadius: 16,
                padding: "10px 8px",
                background: "#fff",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "end",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 28 }}>
                {asset.icon ? (
                  <img src={asset.icon} alt={asset.name} style={asset.iconStyle} />
                ) : asset.label && asset.label.includes("\n") ? (
                  <span style={{ fontSize: 18, fontWeight: 700, color: asset.bg, lineHeight: 1.1, }}>
                    {asset.label.split("\n").map((l, i) => (
                      <span key={i} style={{ display: "block", textAlign: "center" }}>{l}</span>
                    ))}
                  </span>
                ) : (
                  asset.label
                )}
              </span>
              <span
                className=" font-semibold"
                style={{ fontSize: 14, color: "#004c50" }}
              >
                {asset.shortName}
              </span>
            </button>
          ))}
        </div>

        <p
          className="text-center font-normal whitespace-pre-line text-[#004c50]"
          style={{fontSize: 12, marginTop: 18 }}
        >
          {c.footer}
        </p>
      </SheetWrapper>
    );
  }

  if (step === 2 && content.step2) {
    const asset = selectedAsset || GUIDE_ASSETS[0];
    const c = content.step2;
    const heading = typeof c.heading === "function" ? c.heading(asset) : c.heading;
    return (
      <SheetWrapper dim={false}>
        <StepDots active={stage} total={totalDots} />
        <h2
          className="font-bold text-center text-primaryColor"
          style={{ fontSize: 30, marginBottom: 8, paddingInline:30 }}
        >
          {heading}
        </h2>
        <p
          className="text-center whitespace-pre-line text-primaryColor"
          style={{ fontSize: 14, marginBottom: 28 }}
        >
          {c.subtext}
        </p>

        <button
          onClick={() => advanceStep(3)}
          style={{
            width: "100%",
            background: "#004c50",
            color: "#fff",
            borderRadius: 100,
            padding: "16px 0",
            fontWeight: 600,
            fontSize: 17,
            border: "none",
            cursor: "pointer",
          }}
          className=""
        >
          {c.buttonText}
        </button>
      </SheetWrapper>
    );
  }

  if (step === 4) {
    const c = content.step4;
    const handleViewPosition = () => {
      if (content.hasStep5) {
        advanceStep(5);
      } else {
        completeGuide();
      }
    };
    return (
      <SheetWrapper>
        <StepDots active={stage} total={totalDots} />
        <h2
          className="font-bold text-center text-primaryColor"
          style={{ fontSize: 30, marginBottom: 6 }}
        >
          {c.heading}
        </h2>
        <p
          className="text-center font-semibold"
          style={{ color: "#004c50", fontSize: 18, marginBottom: 10 }}
        >
          {c.bold}
        </p>
        <p
          className="text-center whitespace-pre-line"
          style={{ color: "#004c50", fontSize: 14, marginBottom: 28 }}
        >
          {c.subtext}
        </p>

        <button
          onClick={handleViewPosition}
          style={{
            width: "100%",
            background: "#004c50",
            color: "#fff",
            borderRadius: 100,
            padding: "16px 0",
            fontWeight: 600,
            fontSize: 17,
            border: "none",
            cursor: "pointer",
            marginBottom: 16,
          }}
          className=""
        >
          View my position
        </button>

        <button
          onClick={completeGuide}
          style={{
            width: "100%",
            background: "transparent",
            color: "#6B7280",
            border: "none",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
            padding: "8px 0",
          }}
          className=""
        >
          Skip for now
        </button>
      </SheetWrapper>
    );
  }

  if (step === 5 && content.step5) {
    const c = content.step5;
    return (
      <SheetWrapper>
        <StepDots active={stage} total={totalDots} />
        <h2
          className="font-bold text-center text-primaryColor"
          style={{ fontSize: 30, marginBottom: 16 }}
        >
          {c.heading}
        </h2>
        <p
          className="text-center whitespace-pre-line"
          style={{ color: "#004c50", fontSize: 15, marginBottom: c.infoLines ? 16 : 28 }}
        >
          {c.subtext}
        </p>

        {c.infoLines && (
          <div style={{ marginBottom: 24 }}>
            {c.infoLines.map((line, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "6px 4px",
                }}
              >
                <p
                  className=" whitespace-pre-line inline-block"
                  style={{ color: "#004c50", fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: "center" }}
                >
                  {line.text}
                {line.icon === "shield" ? (
                  <img
                    src="/shield.svg"
                    alt="shield"
                    style={{ width: 28, height: 28, flexShrink: 0 }}
                  />
                ) : (
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "2px solid #35b23c",
                      color: "#35b23c",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 16,
                      // fontStyle: "italic",
                      fontFamily: "Georgia, serif",
                      lineHeight: 1,
                      flexShrink: 0,
                      marginLeft: 8,
                    }}
                  >
                    i
                  </span>
                )}
                </p>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={completeGuide}
          style={{
            width: "100%",
            background: "#004c50",
            color: "#fff",
            borderRadius: 100,
            padding: "16px 0",
            fontWeight: 600,
            fontSize: 17,
            border: "none",
            cursor: "pointer",
          }}
          className=""
        >
          {c.buttonText}
        </button>
      </SheetWrapper>
    );
  }

  return null;
}
